import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatDateTime } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { getCapture } from '../../../../api-new/state/notify'
import { channelLabel, providerLabel } from '../../../../shared/notify/words'
import { timelinePath } from './paths'
import './notify.scss'

export const prettyJSON = value => {
  if (value === undefined || value === null) return ''
  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2)
    } catch (e) {
      return value
    }
  }
  return JSON.stringify(value, null, 2)
}

export const bytesText = n => {
  const v = Number(n)
  if (!Number.isFinite(v)) return ''
  if (v < 1024) return `${v} B`
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`
  return `${(v / 1024 / 1024).toFixed(1)} MB`
}

export const MODE_LABEL = { only: 'Captured, not sent', tee: 'Sent and captured' }

// FCM carries the C-5 envelope as a JSON string in data.edxr: decoded here,
// so it reads like the APNs body's.
const fcmEnvelope = body => {
  const raw = body && body.message && body.message.data && body.message.data.edxr
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch (e) {
    return null
  }
}

// One captured payload (C-4 GET /notify/captures/:id, C-2 section 6.2):
// exactly the request APNs, FCM, Web Push or the email provider would have
// received, pretty-printed. Every secret (authorization, device tokens and
// endpoints, p256dh/auth, keys, link secrets) is already elided by the
// server; this page shows what it is given and adds nothing.
export const CaptureDetail = ({ id, onLoaded }) => {
  const [capture, setCapture] = useState(undefined)
  const [error, failure, setError] = useErrorLine()
  const live = useRef(true)
  const loadedRef = useRef(onLoaded)
  loadedRef.current = onLoaded

  useEffect(() => {
    live.current = true
    getCapture(id).then(
      c => {
        if (!live.current) return
        setCapture(c || null)
        if (c && loadedRef.current) loadedRef.current(c)
      },
      err => {
        if (!live.current) return
        setCapture(null)
        setError(serverMessage(err, err && err.status === 404 ? "This capture isn't available. Captures are kept for the delivery retention period." : "This capture couldn't be loaded."), err && err.status === 404 ? null : err)
      }
    )
    return () => {
      live.current = false
    }
  }, [id, setError])

  if (capture === undefined) return <p className="sw-hint m-0" role="status">Loading…</p>
  if (capture === null) return <div><p className="text-danger small m-0" role="alert">{error}</p><ErrorReference error={failure} className="mt-1" /></div>

  const request = capture.request || {}
  const headers = Object.entries(request.headers || {})
  const envelope = capture.provider === 'fcm' ? fcmEnvelope(request.body) : null
  const email = request.email
  const webpush = request.webpush

  return (
    <div className="nf-capture" data-testid="notify-capture">
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <Pill tone="info">{MODE_LABEL[capture.mode] || capture.mode}</Pill>
        <span>{`${providerLabel(capture.provider)} · ${channelLabel(capture.channel)}`}</span>
      </div>
      <p className="sw-hint small nf-secret-note">Secrets, device tokens and keys were already removed by the server before this was stored. What remains is exactly what the provider would have received.</p>

      <dl className="sw-dl">
        <dt>Captured</dt><dd>{formatDateTime(capture.createdAt)}</dd>
        <dt>To</dt><dd className="sw-mono">{capture.addressRef}</dd>
        {capture.deliveryId && <><dt>Delivery</dt><dd className="sw-mono">{capture.deliveryId}</dd></>}
        {capture.loginId && <><dt>Login</dt><dd className="sw-mono">{capture.loginId}</dd></>}
        <dt>Size</dt><dd>{bytesText(capture.requestBytes)}</dd>
        {capture.notificationId && <><dt>Trace id</dt><dd><span className="sw-mono">{capture.notificationId}</span> <Link to={timelinePath(capture.notificationId, true)}>Open the timeline</Link></dd></>}
      </dl>

      {(request.method || request.url) &&
        <section className="ea-panel-section" aria-labelledby={`nf-cap-req-${capture.id}`}>
          <h3 className="h6 fw-semibold" id={`nf-cap-req-${capture.id}`}>Request</h3>
          <p className="sw-mono small mb-2"><span className="fw-semibold me-2">{request.method}</span><span>{request.url}</span></p>
          {headers.length > 0 &&
            <table className="table nf-table" aria-label="Headers">
              <tbody>
                {headers.map(([k, v]) => <tr key={k}><th className="sw-mono">{k}</th><td className="sw-mono">{String(v)}</td></tr>)}
              </tbody>
            </table>}
          {request.body !== undefined && request.body !== null &&
            <>
              <h4 className="h6">Body</h4>
              <pre className="nf-json" aria-label="Body">{prettyJSON(request.body)}</pre>
            </>}
          {envelope &&
            <>
              <h4 className="h6 mt-2">Envelope (data.edxr, decoded)</h4>
              <pre className="nf-json" aria-label="Envelope">{prettyJSON(envelope)}</pre>
            </>}
        </section>}

      {webpush &&
        <section className="ea-panel-section" aria-labelledby={`nf-cap-wp-${capture.id}`}>
          <h3 className="h6 fw-semibold" id={`nf-cap-wp-${capture.id}`}>Web Push</h3>
          <p className="sw-hint small">The plaintext before encryption. The ciphertext changes every time and isn't kept.</p>
          <pre className="nf-json" aria-label="Plaintext">{prettyJSON(webpush.plaintext)}</pre>
          <dl className="sw-dl mt-2">
            {webpush.vapidSubject && <><dt>VAPID subject</dt><dd>{webpush.vapidSubject}</dd></>}
            {webpush.vapidCredentialId && <><dt>Credential</dt><dd className="sw-mono">{webpush.vapidCredentialId}</dd></>}
            {webpush.encryptedBytes !== undefined && <><dt>Encrypted</dt><dd>{`${bytesText(webpush.encryptedBytes)} encrypted`}</dd></>}
            {webpush.subscriptionKeys && <><dt>Subscription keys</dt><dd className="sw-mono">{Object.entries(webpush.subscriptionKeys).map(([k, v]) => `${k} ${v}`).join(' · ')}</dd></>}
          </dl>
        </section>}

      {email &&
        <section className="ea-panel-section" aria-labelledby={`nf-cap-email-${capture.id}`}>
          <h3 className="h6 fw-semibold" id={`nf-cap-email-${capture.id}`}>Email</h3>
          <dl className="sw-dl">
            <dt>Template</dt><dd className="sw-mono">{email.templateKey}</dd>
            {email.providerTemplateEnv && <><dt>Template setting</dt><dd className="sw-mono">{email.providerTemplateEnv}</dd></>}
            <dt>To</dt><dd>{(email.to || []).join(', ')}</dd>
            {email.replyTo && <><dt>Reply to</dt><dd>{email.replyTo}</dd></>}
            {email.subjectRef && <><dt>About</dt><dd className="sw-mono">{email.subjectRef}</dd></>}
          </dl>
          <h4 className="h6 mt-2">Variables</h4>
          <pre className="nf-json" aria-label="Variables">{prettyJSON(email.variables || {})}</pre>
          {email.customArgs &&
            <>
              <h4 className="h6 mt-2">Custom arguments</h4>
              <pre className="nf-json" aria-label="Custom arguments">{prettyJSON(email.customArgs)}</pre>
            </>}
        </section>}
    </div>
  )
}
